/**
 * Copyright 2021 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { type FilterSelectionType } from '@/types/filterTypes';
import { useCallback } from 'react';
import { useLabels } from './useLabels';
import { useGetSessionFiltersList } from './useGetSessionFiltersList';
import { sessionStorage } from '@/utils/sessionStorage';

const labelMap = { lblPriceCustom: 'label_Price_Custom' };

export const useGetFilterAnalytics = (): ((
  selectedFilter: FilterSelectionType,
  isChecked: boolean,
) => string) => {
  const customPriceLabel = useLabels(labelMap);
  // Keep this for tests to use
  const filterSelectionList = useGetSessionFiltersList();
  return useCallback(
    (selectedFilter: FilterSelectionType, isChecked: boolean) => {
      // Use the mocked data in tests, direct sessionStorage in production

      const currentFilters =
        process.env.NODE_ENV === 'test' ? filterSelectionList : parseSessionStorage();
      const updatedFilterSelectionList = setSelectedFilterTypes(
        selectedFilter,
        isChecked,
        currentFilters,
        customPriceLabel.lblPriceCustom,
      );

      const selectedFilters = setFilterAnalytics(updatedFilterSelectionList);
      const joinedSelectedFiltersObject = Object.values(selectedFilters).join(' | ');

      return joinedSelectedFiltersObject;
    },
    [customPriceLabel.lblPriceCustom, filterSelectionList],
  );
};

// Helper function to parse sessionStorage directly
const parseSessionStorage = (): FilterSelectionType[] => {
  const sessionFilterAnalytics = sessionStorage.getItem('filterSelectionAnalytics');
  if (!sessionFilterAnalytics) {
    return [];
  }

  const filterSelections = sessionFilterAnalytics.split(' | ');
  const currentFilterSelectionList: FilterSelectionType[] = [];

  if (filterSelections.length > 0 && filterSelections[0] !== '') {
    filterSelections.forEach((filterSelection) => {
      const filterParts = filterSelection.split(': ');
      if (filterParts.length >= 3) {
        const parsedValues = filterParts[2].split(', ');

        parsedValues.forEach((value) => {
          currentFilterSelectionList.push({
            type: filterParts[0],
            facet: filterParts[1],
            value: value,
          });
        });
      }
    });
  }

  return currentFilterSelectionList;
};
const getSet = (key: string): Set<string> => {
  if (key === 'quickFilters') {
    const raw = sessionStorage.getItem('quickFilters');
    const arr = raw ? (JSON.parse(raw) as string[]) : [];
    return new Set<string>(arr);
  }

  if (key === 'oneClickFilters') {
    const raw = sessionStorage.getItem('oneClickFilters');
    const arr = raw ? (JSON.parse(raw) as string[]) : [];
    return new Set<string>(arr);
  }

  return new Set<string>();
};

const saveSet = (key: string, set: Set<string>): void => {
  if (key === 'quickFilters') {
    sessionStorage.setItem('quickFilters', JSON.stringify([...set]));
    return;
  }

  if (key === 'oneClickFilters') {
    sessionStorage.setItem('oneClickFilters', JSON.stringify([...set]));
    return;
  }
};

const setSelectedFilterTypes = (
  filter: FilterSelectionType,
  isChecked: boolean,
  selectedFilterTypes?: FilterSelectionType[],
  customPriceLabel?: string,
): FilterSelectionType[] | undefined => {
  let filterSelectionList = selectedFilterTypes;

  const existingFilterSelection =
    filterSelectionList?.findIndex(
      (selectedFilter) =>
        selectedFilter.facet === filter.facet && selectedFilter.value === filter.value,
    ) ?? -1;

  const existingCustomPriceFilter =
    filterSelectionList?.findIndex(
      (customPriceFilter) => customPriceFilter.facet === customPriceLabel,
    ) ?? -1;

  const existingFilterIndex =
    filter.facet === customPriceLabel ? existingCustomPriceFilter : existingFilterSelection;

  if (existingFilterIndex !== -1) {
    if (isChecked && filterSelectionList) {
      filterSelectionList[existingFilterIndex] = filter.type
        ? filter
        : filterSelectionList[existingFilterIndex];
    } else {
      filterSelectionList?.splice(existingFilterIndex, 1);
    }
  } else if (isChecked && filter?.facet && filter?.value) {
    const filterValue = filter.value;
    const valueKey = String(filterValue);

    const incomingType = filter.type ?? 'Standard';
    const isQuickFromRedux = incomingType.startsWith('Quick');
    const isOneClickFromRedux = incomingType.startsWith('One Click');

    if (filter?.isStandardFilter) {
      const q = getSet('quickFilters');
      const o = getSet('oneClickFilters');

      const qChanged = q.delete(valueKey);
      const oChanged = o.delete(valueKey);
      if (qChanged) {
        saveSet('quickFilters', q);
      }
      if (oChanged) {
        saveSet('oneClickFilters', o);
      }
    }
    if (isQuickFromRedux) {
      const s = getSet('quickFilters');
      s.add(valueKey);
      saveSet('quickFilters', s);
    }
    if (isOneClickFromRedux) {
      const s = getSet('oneClickFilters');
      s.add(valueKey);
      saveSet('oneClickFilters', s);
    }

    const prev = Array.isArray(filterSelectionList) ? filterSelectionList : [];
    const quickSet = getSet('quickFilters');
    const oneClickSet = getSet('oneClickFilters');
    const resolvedType = oneClickSet.has(valueKey)
      ? 'One Click'
      : quickSet.has(valueKey)
        ? 'Quick Filter'
        : incomingType;
    const newItem: FilterSelectionType = {
      ...filter,
      type: resolvedType,
    };

    filterSelectionList = [...prev, newItem];
  }

  return filterSelectionList;
};

const setFilterAnalytics = (filterSelectionList: FilterSelectionType[] = []) => {
  const selectedFiltersObject = <Record<string, string>>{};

  filterSelectionList?.forEach((selectedFilter) => {
    const filterObjectLabel = `${selectedFilter.type}: ${selectedFilter.facet}`;

    if (!selectedFiltersObject[filterObjectLabel]) {
      selectedFiltersObject[filterObjectLabel] = `${filterObjectLabel}: ${selectedFilter.value}`;
    } else {
      selectedFiltersObject[filterObjectLabel] =
        `${selectedFiltersObject[filterObjectLabel]}, ${selectedFilter.value}`;
    }
  });

  return selectedFiltersObject;
};
